import React, { useState, useEffect } from 'react';
import LoginSelection from './components/LoginSelection';
import StudentDashboard from './components/StudentDashboard';
import TeacherDashboard from './components/TeacherDashboard';
import TuitionTeacherDashboard from './components/TuitionTeacherDashboard';
import Admin1Dashboard from './components/Admin1Dashboard';
import AdminDashboard from './components/AdminDashboard';
import AdminBusinessPanel from './components/AdminBusinessPanel';
import PrincipalDashboard from './components/PrincipalDashboard';
import ParentDashboard from './components/ParentDashboard';
import BlinkitStyleDashboard from './components/BlinkitStyleDashboard';
import HomeworkImageDemo from './components/HomeworkImageDemo';
import EmbeddedGame from './components/EmbeddedGame';
import GlobalLivePlayer from './components/LiveStream/GlobalLivePlayer';
import LiveStatusBadge from './components/LiveStream/LiveStatusBadge';
import DraggableBadge from './components/LiveStream/DraggableBadge';
import { initializeTimezone } from './config/timezone';
import { announcePresence } from './utils/liveSocket';
import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  // Live rendered height (px) of the fixed <GlobalLivePlayer/>; 0 when not live.
  const [liveHeight, setLiveHeight] = useState(0);

  useEffect(() => {
    // Initialize timezone configuration
    initializeTimezone();
    
    const savedUser = localStorage.getItem('user');
    const savedImpersonatedUser = localStorage.getItem('impersonatedUser');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
    if (savedImpersonatedUser) {
      setImpersonatedUser(JSON.parse(savedImpersonatedUser));
    }
  }, []);

  // Start presence tracking as soon as a user is logged in (not while impersonating)
  useEffect(() => {
    if (user && localStorage.getItem('isImpersonating') !== 'true' && !impersonatedUser) {
      announcePresence(user);
    }
  }, [user, impersonatedUser]);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = () => {
    // Check if we're impersonating and need to return to original user
    const isImpersonating = localStorage.getItem('isImpersonating');
    const principalSession = sessionStorage.getItem('principalSession');
    const teacherSession = sessionStorage.getItem('teacherSession');
    const originalPrincipal = localStorage.getItem('originalPrincipal');
    const originalTeacher = localStorage.getItem('originalTeacher');
    const originalTuitionTeacher = sessionStorage.getItem('originalTuitionTeacher');
    const isTuitionTeacherImpersonating = sessionStorage.getItem('isTuitionTeacherImpersonating');
    
    if (isImpersonating === 'true' || isTuitionTeacherImpersonating === 'true') {
      try {
        // Return to tuition teacher session if exists
        if (originalTuitionTeacher && isTuitionTeacherImpersonating === 'true') {
          const tuitionTeacher = JSON.parse(originalTuitionTeacher);
          setUser(tuitionTeacher);
          localStorage.setItem('user', JSON.stringify(tuitionTeacher));
          sessionStorage.removeItem('originalTuitionTeacher');
          sessionStorage.removeItem('isTuitionTeacherImpersonating');
          return;
        }
        
        // Return to principal session if exists
        if (principalSession || originalPrincipal) {
          let principalUser;
          if (principalSession) {
            const principal = JSON.parse(principalSession);
            principalUser = principal.user;
          } else if (originalPrincipal) {
            principalUser = JSON.parse(originalPrincipal);
          }
          
          if (principalUser) {
            setUser(principalUser);
            localStorage.setItem('user', JSON.stringify(principalUser));
            localStorage.removeItem('isImpersonating');
            localStorage.removeItem('originalPrincipal');
            localStorage.removeItem('originalTeacher');
            sessionStorage.removeItem('principalSession');
            sessionStorage.removeItem('teacherSession');
            return;
          }
        }
        
        // Return to teacher session if exists
        if (teacherSession || originalTeacher) {
          let teacherUser;
          if (teacherSession) {
            const teacher = JSON.parse(teacherSession);
            teacherUser = teacher.user;
          } else if (originalTeacher) {
            teacherUser = JSON.parse(originalTeacher);
          }
          
          if (teacherUser) {
            setUser(teacherUser);
            localStorage.setItem('user', JSON.stringify(teacherUser));
            localStorage.removeItem('isImpersonating');
            localStorage.removeItem('originalTeacher');
            sessionStorage.removeItem('teacherSession');
            return;
          }
        }
      } catch (error) {
        console.error('Error returning to original user:', error);
      }
    }
    
    // Complete logout
    setUser(null);
    setImpersonatedUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    localStorage.removeItem('impersonatedUser');
    localStorage.removeItem('isImpersonating');
    localStorage.removeItem('originalPrincipal');
    localStorage.removeItem('originalTeacher');
    sessionStorage.removeItem('principalSession');
    sessionStorage.removeItem('teacherSession');
    sessionStorage.removeItem('originalTuitionTeacher');
    sessionStorage.removeItem('isTuitionTeacherImpersonating');
  };

  const handleImpersonate = (targetUser) => {
    if (user.role === 'teacher_admin_level1') {
      setImpersonatedUser(targetUser);
      localStorage.setItem('impersonatedUser', JSON.stringify(targetUser));
    }
  };

  const handleStopImpersonation = () => {
    setImpersonatedUser(null);
    localStorage.removeItem('impersonatedUser');
  };

  // Check if we're on the homework demo route
  if (window.location.pathname === '/homework-demo') {
    return <HomeworkImageDemo />;
  }

  // Check if we're on the embedded game route
  if (window.location.pathname === '/embedded-game' || window.location.pathname === '/game') {
    return <EmbeddedGame />;
  }

  if (!user) {
    return <LoginSelection onLogin={handleLogin} />;
  }

  const renderDashboard = () => {
    const currentUser = impersonatedUser || user;
    const isImpersonating = impersonatedUser && user.role === 'teacher_admin_level1';
    const isPrincipalImpersonating = localStorage.getItem('isImpersonating') === 'true';
    const isTuitionTeacherImpersonating = sessionStorage.getItem('isTuitionTeacherImpersonating') === 'true';
    
    switch (currentUser.role) {
      case 'student':
        return <StudentDashboard 
          user={currentUser} 
          onLogout={handleLogout} 
          isImpersonated={isPrincipalImpersonating || localStorage.getItem('originalTeacher') !== null || isTuitionTeacherImpersonating}
          impersonationType={isTuitionTeacherImpersonating ? 'tuition_teacher' : (isPrincipalImpersonating ? 'principal' : 'teacher')}
        />;
      case 'teacher':
        return <TeacherDashboard 
          user={currentUser} 
          onLogout={handleLogout} 
          isImpersonating={isImpersonating || isPrincipalImpersonating}
          onStopImpersonation={handleStopImpersonation}
          originalUser={isImpersonating ? user : null}
          isPrincipalImpersonating={isPrincipalImpersonating}
        />;
      case 'teacher_admin_level1':
        return <TeacherDashboard 
          user={currentUser} 
          onLogout={handleLogout} 
          isTeacherAdmin={true}
          onImpersonate={handleImpersonate}
        />;
      case 'tuition_teacher':
        return <TuitionTeacherDashboard user={currentUser} onLogout={handleLogout} />;
      case 'parent':
        return <BlinkitStyleDashboard user={currentUser} onLogout={handleLogout} />;
      case 'admin1':
        return <Admin1Dashboard user={currentUser} onLogout={handleLogout} />;
      case 'admin':
        return <AdminDashboard user={currentUser} onLogout={handleLogout} />;
      case 'admin2':
        return <AdminBusinessPanel user={currentUser} onLogout={handleLogout} />;
      case 'principal':
        return <PrincipalDashboard user={currentUser} onLogout={handleLogout} />;
      default:
        return <LoginSelection onLogin={handleLogin} />;
    }
  };

  // Fixed-position stacking: impersonation bar (z-[70]) > live player (z-[60]).
  const showImpersonationBar = !!(impersonatedUser && user.role === 'teacher_admin_level1');
  const IMPERSONATION_BAR_HEIGHT = showImpersonationBar ? 40 : 0;
  const pagePaddingTop = IMPERSONATION_BAR_HEIGHT + liveHeight;

  return (
    <div className="App">
      {showImpersonationBar && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: IMPERSONATION_BAR_HEIGHT,
          boxSizing: 'border-box',
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
          color: 'white',
          padding: '0 20px',
          zIndex: 70,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '14px',
          fontWeight: 'bold'
        }}>
          <span>🎭 Impersonating: {impersonatedUser.name} ({impersonatedUser.role})</span>
          <button
            onClick={handleStopImpersonation}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              padding: '4px 12px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            ✕ Stop Impersonation
          </button>
        </div>
      )}
      {(localStorage.getItem('isImpersonating') === 'true' || sessionStorage.getItem('isTuitionTeacherImpersonating') === 'true') && (() => {
        const originalTeacher = localStorage.getItem('originalTeacher');
        const originalPrincipal = localStorage.getItem('originalPrincipal');
        const originalTuitionTeacher = sessionStorage.getItem('originalTuitionTeacher');
        const isTuitionTeacherImpersonating = sessionStorage.getItem('isTuitionTeacherImpersonating') === 'true';
        const isTeacherImpersonating = originalTeacher && !originalPrincipal && !isTuitionTeacherImpersonating;
        const isPrincipalImpersonating = originalPrincipal && !isTuitionTeacherImpersonating;
        
        let buttonText = 'Return to Dashboard';
        let buttonColor = 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)';
        let hoverColor = 'linear-gradient(135deg, #0056b3 0%, #004085 100%)';
        let shadowColor = '0 3px 8px rgba(0, 123, 255, 0.3)';
        
        if (isTuitionTeacherImpersonating) {
          buttonText = '← Return to Tuition Teacher Dashboard';
          buttonColor = 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)';
          hoverColor = 'linear-gradient(135deg, #388e3c 0%, #2e7d32 100%)';
          shadowColor = '0 3px 8px rgba(76, 175, 80, 0.3)';
        } else if (isTeacherImpersonating) {
          buttonText = '← Return to Teacher Dashboard';
          buttonColor = 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)';
          hoverColor = 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)';
          shadowColor = '0 3px 8px rgba(139, 92, 246, 0.3)';
        } else if (isPrincipalImpersonating) {
          buttonText = '← Return to Principal Dashboard';
        }
        
        return (
          <button
            onClick={handleLogout}
            style={{
              position: 'fixed',
              top: `${liveHeight + 10}px`, // sits below the live player when it is showing
              left: '10px',
              background: buttonColor,
              color: 'white',
              border: 'none',
              padding: window.innerWidth <= 768 ? '8px 14px' : '8px 14px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: window.innerWidth <= 768 ? '12px' : '12px',
              fontWeight: 'bold',
              minHeight: window.innerWidth <= 768 ? '36px' : '34px',
              boxShadow: shadowColor,
              transition: 'all 0.2s ease',
              zIndex: 10000
            }}
            onMouseEnter={(e) => {
              e.target.style.background = hoverColor;
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = buttonColor;
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {buttonText}
          </button>
        );
      })()}
      {/* Global fixed live class banner/player (student & parent only) */}
      <GlobalLivePlayer
        user={impersonatedUser || user}
        topOffset={IMPERSONATION_BAR_HEIGHT}
        onHeightChange={setLiveHeight}
      />
      {/* Persistent live-status pill (student/parent -> opens player, admin -> stream control) */}
      {user && (
        <DraggableBadge defaultTop={liveHeight + IMPERSONATION_BAR_HEIGHT + 10}>
          <LiveStatusBadge user={impersonatedUser || user} className="shadow-md" />
        </DraggableBadge>
      )}
      {/* Dynamic top padding keeps the dashboard header just below the fixed bars */}
      <div style={{ paddingTop: pagePaddingTop, transition: 'padding-top 300ms ease-in-out' }}>
        {renderDashboard()}
      </div>
    </div>
  );
}

export default App;